import { inject as service } from '@ember/service';
import { TwoFactorDetails } from '@square/dashboard-protos/models/squareup/multipass/common';
import { v4 as uuid } from 'uuid';
import { VerificationCodeRequest } from '@square/dashboard-protos/models/squareup/multipass/external';
import Features from '@square/ember-features/services/features';
import { feature } from '@square/ember-features/macros';
import MultipassExternalApi from '@banking/api/services/multipass-external-api';
import Route from '@ember/routing/route';
import type IntlService from 'ember-intl/services/intl';
import type { StatusResponse } from '@square/dashboard-protos/models/squareup/multipass/http';
import type RouterService from '@ember/routing/router-service';

const THIRTY_MINUTES = 1800000; // 1000 * 60 * 30

export interface Model {
  requiresTwoFactor: boolean;
  twoFactorProvidedTimestamp?: number;
  smsTwoFactor?: TwoFactorDetails;
  googleAuthTwoFactor?: TwoFactorDetails;
  isEnrolling: boolean;
}

export default class TwoFactorRoute extends Route {
  @service features!: Features;
  @service intl!: IntlService;
  @service multipassExternalApi!: MultipassExternalApi;
  @service router!: RouterService;
  @feature('banking/stop_prefilling_savings_onboarding')
  declare stopPrefilling: boolean;

  async model(): Promise<Model> {
    let smsTwoFactor: TwoFactorDetails | undefined;
    let googleAuthTwoFactor: TwoFactorDetails | undefined;

    const status = this.modelFor('application') as StatusResponse;
    const twoFactorProvidedTimestamp = status.twoFactorProvidedTimestamp;
    const requiresTwoFactor = status.requiresTwoFactor;

    if (requiresTwoFactor) {
      const listTwoFactorResponse =
        await this.multipassExternalApi.listTwoFactor();

      const twoFactorDetails = listTwoFactorResponse.twoFactorDetails;

      googleAuthTwoFactor = twoFactorDetails.find(
        (tfd) => tfd.googleauth !== null
      );

      smsTwoFactor = twoFactorDetails.find((tfd) => tfd.sms !== null);
    }

    return {
      requiresTwoFactor,
      twoFactorProvidedTimestamp,
      smsTwoFactor,
      googleAuthTwoFactor,
      isEnrolling: false,
    };
  }

  async redirect(model: Model) {
    if (model.requiresTwoFactor) {
      // enrolled
      if (
        model.twoFactorProvidedTimestamp &&
        model.twoFactorProvidedTimestamp < Date.now() - THIRTY_MINUTES
      ) {
        // re-auth
        if (model.googleAuthTwoFactor) {
          // transition to googleauth 2fa
          this.router.transitionTo('signup.two-factor.google-authenticator');
        } else if (model.smsTwoFactor?.sms) {
          // send sms code
          const response = await this.multipassExternalApi.sendVerificationCode(
            {
              reason: VerificationCodeRequest.Reason.TWO_FACTOR,
              smsPhone: model.smsTwoFactor.sms.phone,
              locale: this.intl.primaryLocale,
              idempotenceKey: uuid(),
            }
          );
          if (response.error) {
            throw response.error;
          }
          // transition to sms 2fa
          this.router.transitionTo('signup.two-factor.sms');
        } else {
          // TODO ask auth about webauth type
        }
      } else {
        // proceed - redirect - happy path
        if (this.stopPrefilling) {
          this.router.transitionTo('signup.enter-identity-info');
        } else {
          this.router.transitionTo('signup.confirm-identity');
        }
      }
    } else {
      // enroll - redirect or render?
      model.isEnrolling = true;
      this.router.transitionTo('signup.two-factor.enroll');
    }
  }
}
